import React, { CSSProperties, useState } from "react";
import {
  IGeoSearchFiltersValidBackendValues,
  IGeoSearchFiltersValue
} from "../GeoSearchFilters";
import Modal from "../../Modal";
import { DialogWrapper } from "../../containers";
import { TitleH2 } from "../../generic";
import { Button as AntButton, DatePicker, Input, Select, Tooltip } from "antd";
import styled from "styled-components/macro";
import { InfoCircleOutlined } from "@ant-design/icons";
import PlacesAutocomplete from "../PlacesAutocomplete";
import dayjs from "dayjs";
import { findClosestRadius, isPositiveInteger } from "../utils";
import { useTranslation } from "react-i18next";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { DATE_PICKER_DATE_FORMAT } from "../../../constants";

const PreferencesList = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center; // center items on the horizontal line
  align-content: start; // align items to the top on the vertical line
  margin-top: 8px;
`;

const PreferenceValueBoxRoot = styled.div`
  background: ${(p) => p.theme.colors.bgSoftGray};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;

  border-radius: 8px;
  flex-grow: 1;
`;
const PreferenceValueBoxLabel = styled.div`
  color: ${(p) => p.theme.colors.midGrey};
  font-size: 14px;
`;
const PreferenceValueBoxValue = styled.div`
  font-size: 14px;
`;
const Info = styled.div``;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  button {
    flex: 1;
    margin: 5px;
    padding: 10px;
  }
`;

const ALLOWED_RADII = [5, 10, 20, 50, 100, 200];
const ALLOWED_RADII_OPTIONS = ALLOWED_RADII.map((r) => ({
  label: r,
  value: r
}));

const PreferenceValueBox: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  tooltip?: React.ReactNode;
  style?: CSSProperties;
}> = ({ label, value, tooltip, style }) => {
  return (
    <PreferenceValueBoxRoot style={style}>
      <PreferenceValueBoxLabel>
        {label}{" "}
        {tooltip ? (
          <Tooltip title={tooltip} placement="bottom">
            <InfoCircleOutlined
              style={{ marginLeft: "8px", cursor: "pointer" }}
            />
          </Tooltip>
        ) : null}
      </PreferenceValueBoxLabel>
      <PreferenceValueBoxValue>{value}</PreferenceValueBoxValue>
    </PreferenceValueBoxRoot>
  );
};

const DateRangeContainer = styled.div`
  display: flex;
  gap: 10px;
`;

export const formatRadius = (radius: number) => {
  const inKm = radius / 1000;

  if (inKm > 1) {
    return `+${Math.ceil(inKm)} km`;
  }

  return `+${Math.ceil(radius / 100) * 100} m`;
};

export const SaveSearchPreferencesModal: React.FC<{
  preferences: IGeoSearchFiltersValue;
  onClose: () => void;
  onSubmit: (value: IGeoSearchFiltersValidBackendValues) => void;
  isShow: boolean;
  isLoading: boolean;
  inputSize?: SizeType;
}> = ({
  isShow,
  isLoading,
  onClose,
  preferences,
  onSubmit,
  inputSize = "medium" as SizeType
}) => {
  const [editedPreferences, setEditedPreferences] =
    useState<IGeoSearchFiltersValue>({
      ...preferences,
      radius: findClosestRadius(
        preferences.radius,
        ALLOWED_RADII.map((r) => r * 1000)
      )
    });
  const { t } = useTranslation("geomap");
  return (
    <Modal handleClose={onClose} isShow={isShow}>
      <DialogWrapper>
        <TitleH2>{t("saveSearchPreferencesModal.title")}</TitleH2>
        <div>
          <Info>{t("saveSearchPreferencesModal.notification")}</Info>
          <PreferencesList>
            <PreferenceValueBox
              label={t("geoSearchFilters.city")}
              value={
                <PlacesAutocomplete
                  size={inputSize}
                  displayValue={
                    editedPreferences.location && editedPreferences.locationName
                      ? {
                          location: editedPreferences.location,
                          displayName: editedPreferences.locationName
                        }
                      : undefined
                  }
                  error={!editedPreferences.location}
                  allowClear={false}
                  onChange={(newLocation, data) => {
                    setEditedPreferences((prev) => {
                      return {
                        ...prev,
                        location: newLocation,
                        locationName: data?.description
                      };
                    });
                  }}
                />
              }
            />
            <PreferenceValueBox
              label={t("geoSearchFilters.range")}
              value={
                <Select
                  value={
                    editedPreferences.radius
                      ? findClosestRadius(
                          editedPreferences.radius / 1000,
                          ALLOWED_RADII
                        )
                      : ALLOWED_RADII[0]
                  }
                  size={inputSize}
                  onChange={(value) => {
                    setEditedPreferences((prev) => ({
                      ...prev,
                      radius: value * 1000
                    }));
                  }}
                  options={ALLOWED_RADII_OPTIONS}
                />
              }
            />
            <PreferenceValueBox
              label={t("geoSearchFilters.minSize")}
              style={{
                width: "140px"
              }}
              value={
                <Input
                  suffix="m2"
                  placeholder={t("geoSearchFilters.minSize")}
                  min={0}
                  value={editedPreferences.area ?? ""}
                  allowClear
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  size={inputSize}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setEditedPreferences((prev) => ({
                        ...prev,
                        area: undefined
                      }));
                      return;
                    }

                    if (!isPositiveInteger(e.target.value)) {
                      return;
                    }
                    const parsedValue = Number(e.target.value);
                    setEditedPreferences((prev) => ({
                      ...prev,
                      area: parsedValue
                    }));
                  }}
                />
              }
            />
            <PreferenceValueBox
              label={t("geoSearchFilters.maxPrice")}
              style={{
                width: "140px"
              }}
              value={
                <Input
                  suffix="€"
                  placeholder={t("geoSearchFilters.maxPrice")}
                  min={0}
                  value={editedPreferences.price ?? ""}
                  allowClear
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  size={inputSize}
                  onChange={(e) => {
                    if (e.target.value === "") {
                      setEditedPreferences((prev) => ({
                        ...prev,
                        price: undefined
                      }));
                      return;
                    }

                    if (!isPositiveInteger(e.target.value)) {
                      return;
                    }
                    const parsedValue = Number(e.target.value);
                    setEditedPreferences((prev) => ({
                      ...prev,
                      price: parsedValue
                    }));
                  }}
                />
              }
            />

            <PreferenceValueBox
              label=""
              style={{
                flexGrow: "initial"
              }}
              value={
                <DateRangeContainer>
                  <DatePicker
                    size={inputSize}
                    allowClear={false}
                    placeholder={t("geoSearchFilters.startDate")}
                    value={
                      editedPreferences.dateFrom
                        ? dayjs(editedPreferences.dateFrom)
                        : null
                    }
                    format={DATE_PICKER_DATE_FORMAT}
                    onChange={(_date) => {
                      const date = _date?.toDate();
                      setEditedPreferences((prev) => ({
                        ...prev,
                        dateFrom: date,
                        dateTo:
                          date &&
                          prev.dateTo &&
                          date.getTime() > prev.dateTo.getTime()
                            ? undefined
                            : prev.dateTo
                      }));
                    }}
                    style={{ width: "150px" }}
                    status={!editedPreferences.dateFrom ? "error" : ""}
                  />
                  <DatePicker
                    size={inputSize}
                    allowClear
                    placeholder={t("geoSearchFilters.endDate")}
                    format={DATE_PICKER_DATE_FORMAT}
                    defaultValue={
                      editedPreferences.dateFrom
                        ? dayjs(editedPreferences.dateFrom)
                        : undefined
                    }
                    value={
                      editedPreferences.dateTo
                        ? dayjs(editedPreferences.dateTo)
                        : null
                    }
                    disabledDate={(date) => {
                      if (!editedPreferences.dateFrom) {
                        return false;
                      }
                      return (
                        date.toDate().getTime() <
                        editedPreferences.dateFrom?.getTime()
                      );
                    }}
                    onChange={(date) => {
                      setEditedPreferences((prev) => ({
                        ...prev,
                        dateTo: date?.toDate()
                      }));
                    }}
                    style={{ width: "150px" }}
                  />
                </DateRangeContainer>
              }
            />
          </PreferencesList>
        </div>
        <ButtonContainer>
          <AntButton type="default" onClick={onClose} size="large">
            {t("saveSearchPreferencesModal.cancel")}
          </AntButton>
          <AntButton
            type={"primary"}
            onClick={() => {
              if (
                !editedPreferences.location ||
                !editedPreferences.locationName
              ) {
                return;
              }

              onSubmit(
                editedPreferences as IGeoSearchFiltersValidBackendValues
              );
            }}
            loading={isLoading}
            disabled={
              !editedPreferences.location ||
              !editedPreferences.locationName ||
              !editedPreferences.dateFrom
            }
            size="large"
          >
            {t("saveSearchPreferencesModal.saveSearch")}
          </AntButton>
        </ButtonContainer>
      </DialogWrapper>
    </Modal>
  );
};
